import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VIDEO from 'assets/welcome-video.mp4';
import VIDEO_MOBILE from 'assets/m_welcome-video.mp4';
import { authPrimaryButtonSx } from 'component/Input/darkFieldStyles';
import LogoText from 'component/Logo';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

const ONBOARDING_STEPS = [
    {
        Icon: AddCircleOutlineIcon,
        title: 'Add coins',
        detail: 'Build your portfolio from the Assets table below.',
    },
    {
        Icon: CategoryOutlinedIcon,
        title: 'Pick a category',
        detail: 'Sort into BlueChip, DeFi, Gaming, and more.',
    },
    {
        Icon: TrendingUpIcon,
        title: 'Track your XGain',
        detail: 'Watch invested, worth, and profit multiply in real time.',
    },
];

const StepRow = ({ Icon, title, detail, index }) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(149, 242, 5, 0.12)',
                border: '1px solid rgba(149, 242, 5, 0.35)',
                color: '#95F205',
                fontSize: 13,
                fontWeight: 700,
            }}
        >
            {index + 1}
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                <Icon sx={{ fontSize: 16, color: '#98C9F6' }} />
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                    {title}
                </Typography>
            </Stack>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.45 }}>
                {detail}
            </Typography>
        </Box>
    </Stack>
);

const WelcomeModal = ({ handleClose, variant }) => {
    const [open, setOpen] = React.useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector(state => state.user);

    const isOnboarding = variant === 'onboarding' || location.pathname.startsWith('/dashboard');
    const isHome = location.pathname === '/';
    const firstName = user?.firstname || user?.name?.split(' ')[0] || '';

    React.useEffect(() => {
        if (open && variant === 'onboarding') {
            window.dispatchEvent(new CustomEvent('xgain:welcome-opened'));
        }
    }, [open, variant]);

    const close = React.useCallback(() => {
        setOpen(false);
        if (variant === 'onboarding') {
            window.dispatchEvent(new CustomEvent('xgain:welcome-closed'));
        }
        handleClose(false);
    }, [handleClose, variant]);

    React.useEffect(() => {
        if (!open) return;
        const onKey = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                close();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, close]);

    const handlePrimary = () => {
        if (isHome) {
            navigate('/register');
            return;
        }
        close();
    };

    const primaryLabel = isHome ? 'Join Now' : 'Get Started';
    const showLogo = !isOnboarding || !firstName;
    const headline = isOnboarding && firstName
        ? `Welcome, ${firstName}`
        : isOnboarding
            ? 'Welcome aboard'
            : 'Your edge in crypto';
    const subheadline = isOnboarding
        ? 'Your portfolio dashboard is ready. Here is how to get the most out of XGain.'
        : 'Track performance, organize by category, and watch your XGain multiplier grow.';

    return (
        <Modal
            keepMounted
            open={open}
            onClose={close}
            closeAfterTransition
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(0, 0, 0, 0.78)',
                },
            }}
            aria-labelledby="welcome-modal-title"
            aria-describedby="welcome-modal-description"
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '92vw', sm: 500, md: 540 },
                        maxHeight: '90vh',
                        overflow: 'auto',
                        outline: 'none',
                        borderRadius: 4,
                        bgcolor: '#1a1d28',
                        backgroundImage: `
                            radial-gradient(ellipse 80% 50% at 50% -10%, rgba(149, 242, 5, 0.14), transparent 55%),
                            linear-gradient(165deg, #2a3348 0%, #1a1d28 48%, #151821 100%)
                        `,
                        border: '1px solid rgba(149, 242, 5, 0.22)',
                        boxShadow: '0 28px 90px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
                    }}
                >
                    <IconButton
                        onClick={close}
                        aria-label="Close welcome modal"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            color: 'rgba(255,255,255,0.7)',
                            bgcolor: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            width: 34,
                            height: 34,
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.12)',
                                color: '#fff',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>

                    <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 2.5, sm: 3 } }}>
                        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                            {showLogo && (
                                <Box sx={{ mb: 1 }}>
                                    <LogoText fontSize="2rem" fontSizeTm="0.65rem" />
                                </Box>
                            )}

                            <Typography
                                id="welcome-modal-title"
                                variant="h5"
                                sx={{
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: { xs: '1.35rem', sm: '1.55rem' },
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.25,
                                    mb: 1,
                                }}
                            >
                                {headline}
                                {isOnboarding && firstName && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            fontSize: '0.92em',
                                            fontWeight: 600,
                                            color: 'rgba(255,255,255,0.85)',
                                        }}
                                    >
                                        to <LogoText fontSize="1.35rem" fontSizeTm="0.55rem" />
                                    </Box>
                                )}
                            </Typography>

                            <Typography
                                id="welcome-modal-description"
                                sx={{
                                    color: 'rgba(255,255,255,0.68)',
                                    fontSize: { xs: 14, sm: 15 },
                                    lineHeight: 1.55,
                                    maxWidth: 420,
                                    mx: 'auto',
                                }}
                            >
                                {subheadline}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                position: 'relative',
                                borderRadius: 3,
                                overflow: 'hidden',
                                mb: isOnboarding ? 2.5 : 3,
                                border: '1px solid rgba(255,255,255,0.08)',
                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: 'none',
                                    boxShadow: 'inset 0 0 0 1px rgba(149, 242, 5, 0.08)',
                                    borderRadius: 3,
                                },
                            }}
                        >
                            <Box
                                component="video"
                                src={isMobile ? VIDEO_MOBILE : VIDEO}
                                playsInline
                                loop
                                autoPlay
                                muted
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    aspectRatio: '16 / 9',
                                    objectFit: 'cover',
                                    bgcolor: '#0d0f14',
                                }}
                            />
                        </Box>

                        {isOnboarding && (
                            <Stack spacing={1.75} sx={{ mb: 3 }}>
                                {ONBOARDING_STEPS.map((step, index) => (
                                    <StepRow key={step.title} {...step} index={index} />
                                ))}
                            </Stack>
                        )}

                        <Stack spacing={1.25} alignItems="center">
                            <Button
                                onClick={handlePrimary}
                                variant="contained"
                                disableElevation
                                fullWidth
                                sx={{
                                    ...authPrimaryButtonSx,
                                    maxWidth: 320,
                                    py: 1.35,
                                    fontSize: 15,
                                }}
                            >
                                {primaryLabel}
                            </Button>

                            {!isHome && (
                                <Button
                                    onClick={close}
                                    sx={{
                                        color: 'rgba(255,255,255,0.55)',
                                        fontSize: 13,
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: 'rgba(255,255,255,0.85)',
                                            bgcolor: 'transparent',
                                        },
                                    }}
                                >
                                    I will explore on my own
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default WelcomeModal;
